import {getTestProps} from '@benchling/js-test-utils';
import {Button, Tooltip} from '@mantine/core';
import React from 'react';
import {NodeToolbar} from 'reactflow';

import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {
  FLOWCHART_ICON_BUTTON_PROPS,
  useFlowchartIconButtonStyles,
} from 'ui-library/reactflow/buttons/flowchartButtonStyles';

export interface FlowchartNodeToolbarButton {
  disabled?: boolean;
  elementName: string;
  ariaLabel?: string;
  icon: BenchlingIconName;
  onClick: () => void;
  tooltip?: string;
}

export interface FlowchartNodeToolbarProps {
  /**
   * Selected nodes' IDs.
   *
   * If `nodeIds` is specified, the toolbar will position itself relative to the specified nodes; otherwise,
   * `reactflow` will automatically position the toolbar beside the selected node, if a single node is
   * selected.
   *
   * Pass `nodeIds` if the toolbar should show when multiple nodes are selected.
   *
   * If the toolbar should only appear next to a single, selected node, `nodeIds` can be omitted.
   *
   * See: https://reactflow.dev/api-reference/components/node-toolbar
   */
  nodeIds?: Array<string>;
  /**
   * Buttons to show in the toolbar.
   */
  buttons: Array<FlowchartNodeToolbarButton>;
}

export default function FlowchartNodeToolbar({
  nodeIds,
  buttons,
}: FlowchartNodeToolbarProps): JSX.Element | null {
  const {classes} = useFlowchartIconButtonStyles();

  if (buttons.length === 0) {
    return null;
  }

  return (
    <NodeToolbar
      nodeId={nodeIds}
      // If explicit `nodeIds` are passed, control visibility based on `nodeIds`. Otherwise, allow `reactflow`
      // to control visibility, based on if a node is selected.
      isVisible={nodeIds != null ? nodeIds.length > 0 : undefined}
      {...getTestProps({component: 'FlowchartNodeToolbar', element: 'root'})}
    >
      <Button.Group>
        {buttons.map((button) => (
          <Tooltip
            key={button.elementName}
            label={button.tooltip ?? ''}
            disabled={button.tooltip == null}
            withinPortal={true}
          >
            <Button
              {...FLOWCHART_ICON_BUTTON_PROPS}
              // Mantine requires `data-disabled` instead of `disabled` to show tooltips on disabled buttons.
              // See https://v6.mantine.dev/core/button/#disabled-button-with-tooltip
              data-disabled={button.disabled ? true : undefined}
              aria-disabled={button.disabled ? true : undefined}
              aria-label={button.ariaLabel ?? undefined}
              tabIndex={button.disabled ? -1 : undefined}
              onClick={
                button.disabled
                  ? (event) => {
                      event.preventDefault();
                    }
                  : button.onClick
              }
              classNames={{root: classes.root}}
              sx={{'&[data-disabled]': {pointerEvents: 'all'}}}
              {...getTestProps({component: 'FlowchartNodeToolbarButton', element: button.elementName})}
            >
              <BenchlingIcon icon={button.icon} />
            </Button>
          </Tooltip>
        ))}
      </Button.Group>
    </NodeToolbar>
  );
}
